<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, useTemplateRef, watch } from "vue";

import BuildingIcon from "@/assets/icons/building.svg?component";
import {
  isNodeDataOrganization,
  isNodeDataSite,
  TUserPerimeterOrganizationTreeNode,
} from "@/components/data/dashboard/user-perimeter-tree/user-perimeter-tree.types";
import { IBaseTreeExpose } from "@/components/ui/tree/base-tree.types";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import {
  TOrganizationPerimeter,
  TSitePerimeter,
} from "@/data/api/kili/consumption/types/user-organization-perimeter.type";
import { useTypedIntl } from "@/locales/typed-intl";
import { useDashboardStore } from "@/stores/dashboard/dashboard-store";
import { safeParseInt } from "@/utils/number";

import Skeleton from "primevue/skeleton";

import IndeterminateCheckbox from "@/components/ui/checkbox/indeterminated-checkbox.vue";
import BaseTree from "@/components/ui/tree/base-tree.vue";

interface IProps {
  userOrganizationPerimeter?: TOrganizationPerimeter[];
  disabled?: boolean;
  isLoading?: boolean;
}

const props = defineProps<IProps>();

const { t, k } = useTypedIntl();

const dashboardStore = useDashboardStore();
const { organizationPerimeter } = storeToRefs(dashboardStore);

const baseTree = useTemplateRef<IBaseTreeExpose<TOrganizationPerimeter | TSitePerimeter>>("baseTree");

const NODE_HEIGHT = 40;

const selectedDivisions = defineModel<TOrganizationPerimeter[]>("selectedDivisions", {
  required: true,
});

const userPerimeterNodes = computed<TUserPerimeterOrganizationTreeNode[]>(() => {
  if (!props.userOrganizationPerimeter) return [];
  return mapToTreeNode(props.userOrganizationPerimeter);
});

function mapSiteToNode(site: TSitePerimeter): TUserPerimeterOrganizationTreeNode {
  return {
    key: `SITE-${site.siteId}`,
    id: `SITE-${site.siteId}`,
    name: site.siteName,
    data: site,
    children: [],
    state: {
      expanded: true,
      disabled: true,
    },
  };
}

function mapOrganizationToNode(org: TOrganizationPerimeter): TUserPerimeterOrganizationTreeNode {
  return {
    key: `ORGANIZATION-${org.organizationId}`,
    id: `ORGANIZATION-${org.organizationId}`,
    name: org.organizationName,
    data: org,
    children: [...mapToTreeNode(org.children), ...mapSitesToTreeNode(org.sites)],
    state: {
      expanded: true,
    },
  };
}

function mapSitesToTreeNode(
  organizationSitesPerimeter: TOrganizationPerimeter["sites"],
): TUserPerimeterOrganizationTreeNode[] {
  return organizationSitesPerimeter.map(mapSiteToNode);
}

function mapToTreeNode(organizationPerimeter: TOrganizationPerimeter[]): TUserPerimeterOrganizationTreeNode[] {
  return organizationPerimeter.map(mapOrganizationToNode);
}

function handleSelectDivisions(selection: TUserPerimeterOrganizationTreeNode[] | TUserPerimeterOrganizationTreeNode) {
  if (!Array.isArray(selection)) return;

  selectedDivisions.value = selection.filter(isNodeDataOrganization).map((node) => node.data);
}

function isNodeSelected(node: TUserPerimeterOrganizationTreeNode): boolean | null {
  if (!node.data || !isNodeDataOrganization(node)) {
    return false;
  }

  if (selectedDivisions.value.find((d) => d.organizationId === safeParseInt(node.data.organizationId))) {
    return true;
  }

  if (node.children?.some((child: TUserPerimeterOrganizationTreeNode) => isNodeSelected(child) !== false)) {
    return null;
  }

  return false;
}

function searchFilter(query: string, node: TUserPerimeterOrganizationTreeNode) {
  const nodeDataName: string = isNodeDataOrganization(node)
    ? node.data.organizationName
    : isNodeDataSite(node)
      ? node.data.siteName
      : "";

  return nodeDataName.toLocaleLowerCase().includes(query.toLowerCase());
}

watch(
  [userPerimeterNodes, baseTree],
  ([_, tree]) => {
    tree?.select((node) => {
      if (!isNodeDataOrganization(node)) return false;
      return !!organizationPerimeter.value?.find((o) => o.organizationId === node.data.organizationId);
    });
  },
  { immediate: true },
);
</script>

<template>
  <Skeleton
    v-if="props.isLoading"
    class="!h-[20rem]"
  />

  <BaseTree
    v-else
    ref="baseTree"
    class="user-perimeter-tree"
    :collapsed="false"
    :disabled="props.disabled"
    enable-select-all
    :filter="searchFilter"
    :node-height="NODE_HEIGHT"
    :nodes="userPerimeterNodes"
    parents-only
    selection-mode="multiple"
    use-chips
    @selection="handleSelectDivisions($event)"
  >
    <template #selectAllLabel>
      <div class="flex items-center gap-2">
        <BuildingIcon />
        <span>{{ t(k.global.allSites) }}</span>
      </div>
    </template>

    <template #default="node">
      <div class="flex w-full justify-between content-center items-center">
        <div class="flex gap-2 items-center">
          <BuildingIcon v-if="isNodeDataOrganization(node)" />
          <span>{{ node.name }}</span>
        </div>
        <template v-if="isNodeDataOrganization(node)">
          <IndeterminateCheckbox
            class="main-checked-box"
            :data-test-id="DATA_TEST_IDS.components.userPerimeterTree.mainCheckbox"
            :model-value="isNodeSelected(node)"
            readonly
          />
          <IndeterminateCheckbox
            class="disabled-checked-box"
            :data-test-id="DATA_TEST_IDS.components.userPerimeterTree.disabledCheckbox"
            :model-value="true"
            readonly
          />
        </template>
      </div>
    </template>
  </BaseTree>
</template>

<style lang="scss">
.user-perimeter-tree {
  .tree-node-content {
    &.tree-node-disabled {
      .main-checked-box {
        @apply hidden;
      }

      .disabled-checked-box {
        @apply visible;
      }
    }

    &:not(.tree-node-disabled) {
      .main-checked-box {
        @apply visible;
      }

      .disabled-checked-box {
        @apply hidden;
      }
    }
  }
}
</style>
